import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as api from "@/lib/api";
import type {
  SponsorDashboardResponse,
  SponsorSchoolCoverage,
  SponsorTimelineEntry,
  SponsorLeadConfigPublic,
  SponsorLeadSchoolPublic,
  LeadSubmissionPublic,
  PlacementStatus,
  AssetCategory,
} from "@dugads/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LeadSubmissionsTable } from "@/components/lead-submissions-table";
import { MetricCard } from "@/components/metric-card";
import { BarChart3, Building2, MapPin, Receipt, AlertTriangle, Zap, Upload, Trash2, Image, GraduationCap, Plus, X } from "lucide-react";

const statusColors: Record<PlacementStatus, string> = {
  active: "bg-green-500",
  reserved: "bg-blue-500",
  expired: "bg-muted-foreground",
  cancelled: "bg-destructive",
};

const statusBadgeVariant: Record<PlacementStatus, "default" | "secondary" | "destructive" | "outline"> = {
  active: "default",
  reserved: "secondary",
  expired: "outline",
  cancelled: "destructive",
};

const categoryLabels: Record<AssetCategory, string> = {
  barrier: "Barriers",
  dugout: "Dugouts",
  pavilion: "Pavilions",
  jersey: "Jerseys",
  other: "Other",
};

export default function SponsorDashboardPage() {
  const [dashboard, setDashboard] = useState<SponsorDashboardResponse | null>(null);
  const [schools, setSchools] = useState<SponsorSchoolCoverage[]>([]);
  const [timeline, setTimeline] = useState<SponsorTimelineEntry[]>([]);
  const [leadConfigs, setLeadConfigs] = useState<SponsorLeadConfigPublic[]>([]);
  const [leads, setLeads] = useState<LeadSubmissionPublic[]>([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [uploadingLogo, setUploadingLogo] = useState<string | null>(null);
  const [schoolsByConfig, setSchoolsByConfig] = useState<Record<string, SponsorLeadSchoolPublic[]>>({});
  const [addingSchool, setAddingSchool] = useState<string | null>(null); // configId currently adding to
  const [schoolSearch, setSchoolSearch] = useState("");
  const [schoolResults, setSchoolResults] = useState<{ id: string; name: string }[]>([]);
  const [searchingSchools, setSearchingSchools] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const [dashRes, schoolsRes, timelineRes] = await Promise.all([
          api.getSponsorDashboard(),
          api.getSponsorDashboardSchools(),
          api.getSponsorDashboardTimeline(12),
        ]);
        setDashboard(dashRes);
        setSchools(schoolsRes.schools);
        setTimeline(timelineRes.entries);

        // Load lead generation data
        const sponsorId = dashRes.sponsor.id;
        const [configsRes, leadsRes] = await Promise.all([
          api.getSponsorLeadConfigs(sponsorId).catch(() => ({ configs: [] as SponsorLeadConfigPublic[] })),
          api.getSponsorLeads(sponsorId).catch(() => ({ leads: [] as LeadSubmissionPublic[], total: 0 })),
        ]);
        setLeadConfigs(configsRes.configs);
        setLeads(leadsRes.leads);
        setLeadsTotal(leadsRes.total);

        // Load school assignments per config
        const schoolMap: Record<string, SponsorLeadSchoolPublic[]> = {};
        await Promise.all(
          configsRes.configs.map(async (c) => {
            const res = await api.getSponsorLeadSchools(sponsorId, c.id).catch(() => ({ schools: [] as SponsorLeadSchoolPublic[] }));
            schoolMap[c.id] = res.schools;
          }),
        );
        setSchoolsByConfig(schoolMap);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleLogoUpload(configId: string, file: File) {
    if (!dashboard) return;
    setUploadingLogo(configId);
    try {
      const res = await api.uploadLeadConfigLogo(dashboard.sponsor.id, configId, file);
      setLeadConfigs((prev) => prev.map((c) => (c.id === configId ? res.config : c)));
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to upload logo");
    } finally {
      setUploadingLogo(null);
    }
  }

  async function handleDeleteLogo(configId: string) {
    if (!dashboard || !confirm("Remove this logo?")) return;
    try {
      const res = await api.deleteLeadConfigLogo(dashboard.sponsor.id, configId);
      setLeadConfigs((prev) => prev.map((c) => (c.id === configId ? res.config : c)));
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to delete logo");
    }
  }

  async function handleSearchSchools(configId: string, query: string) {
    setSchoolSearch(query);
    if (query.length < 2) { setSchoolResults([]); return; }
    setSearchingSchools(true);
    try {
      const res = await api.searchSchools(query, { limit: 10 });
      const assigned = new Set((schoolsByConfig[configId] ?? []).map((s) => s.school_id));
      setSchoolResults(
        res.schools
          .map((s) => ({ id: s.id, name: s.display_name ?? s.name }))
          .filter((s) => !assigned.has(s.id)),
      );
    } catch {
      setSchoolResults([]);
    } finally {
      setSearchingSchools(false);
    }
  }

  async function handleAssignSchool(configId: string, schoolId: string) {
    if (!dashboard) return;
    try {
      await api.assignLeadSchool(dashboard.sponsor.id, configId, {
        school_id: schoolId,
        placement_slot: "event_detail_footer",
      });
      // Refresh assignments for this config
      const res = await api.getSponsorLeadSchools(dashboard.sponsor.id, configId);
      setSchoolsByConfig((prev) => ({ ...prev, [configId]: res.schools }));
      setAddingSchool(null);
      setSchoolSearch("");
      setSchoolResults([]);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to assign school");
    }
  }

  async function handleRemoveSchool(configId: string, schoolId: string) {
    if (!dashboard) return;
    try {
      await api.removeLeadSchool(dashboard.sponsor.id, configId, schoolId);
      setSchoolsByConfig((prev) => ({
        ...prev,
        [configId]: (prev[configId] ?? []).filter((s) => s.school_id !== schoolId),
      }));
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to remove school");
    }
  }

  if (loading) {
    return <p className="text-muted-foreground" data-page="sponsor-dashboard" data-state="loading">Loading dashboard...</p>;
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive" data-page="sponsor-dashboard" data-state="error" data-error>
        {error}
      </div>
    );
  }

  if (!dashboard) return null;

  const { sponsor, metrics } = dashboard;

  return (
    <div className="flex flex-col gap-6" data-page="sponsor-dashboard" data-state="ready">
      {/* Header */}
      <div className="flex items-center gap-4">
        {sponsor.image_url && (
          <img
            src={sponsor.image_url}
            alt={sponsor.name}
            className="h-12 w-12 rounded-md object-contain border border-border"
          />
        )}
        <div>
          <h1 className="text-2xl font-semibold">{sponsor.name}</h1>
          <p className="text-muted-foreground">Sponsor Dashboard</p>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-region="metrics">
        <MetricCard
          icon={Receipt}
          label="Active Placements"
          value={metrics.active_placements}
        />
        <MetricCard
          icon={Building2}
          label="Schools Reached"
          value={metrics.schools_reached}
        />
        <MetricCard
          icon={MapPin}
          label="Courts Covered"
          value={metrics.courts_covered}
        />
        <MetricCard
          icon={AlertTriangle}
          label="Expiring Soon (30d)"
          value={metrics.upcoming_expirations_30d}
          highlight={metrics.upcoming_expirations_30d > 0}
        />
      </div>

      {/* Status & Category Breakdown */}
      <div className="grid gap-4 sm:grid-cols-2">
        {/* By Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Placements by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {(Object.entries(metrics.by_status) as [PlacementStatus, number][]).map(([status, count]) => (
                <div key={status} className="flex items-center gap-2">
                  <span className={`h-3 w-3 rounded-full ${statusColors[status]}`} />
                  <span className="text-sm capitalize">{status}</span>
                  <span className="text-sm font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* By Category */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Placements by Asset Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {(Object.entries(metrics.by_category) as [AssetCategory, number][])
                .filter(([, count]) => count > 0)
                .map(([category, count]) => (
                  <div key={category} className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5">
                    <BarChart3 className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm">{categoryLabels[category]}</span>
                    <span className="text-sm font-semibold">{count}</span>
                  </div>
                ))}
              {Object.values(metrics.by_category).every((c) => c === 0) && (
                <p className="text-sm text-muted-foreground">No placements yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Schools Coverage */}
      {schools.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">School Coverage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>School</TableHead>
                    <TableHead className="text-right">Active</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Courts</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schools.map((s) => (
                    <TableRow key={s.school_id}>
                      <TableCell className="font-medium">
                        {s.school_name}
                        {s.school_abbreviation && (
                          <span className="ml-1 text-muted-foreground">({s.school_abbreviation})</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{s.active_placements}</TableCell>
                      <TableCell className="text-right">{s.total_placements}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {s.courts.join(", ")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      {timeline.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Placement Timeline</CardTitle>
            <Link
              to="/placements"
              className="text-sm text-primary hover:underline"
            >
              View all placements
            </Link>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2">
              {timeline.map((entry) => (
                <div
                  key={entry.placement_id}
                  className="flex items-center gap-3 rounded-md border border-border px-3 py-2"
                >
                  <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${statusColors[entry.status]}`} />
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-medium">{entry.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {entry.start_date} → {entry.end_date}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant={statusBadgeVariant[entry.status]} className="capitalize">
                      {entry.status}
                    </Badge>
                    {entry.is_expiring_soon && (
                      <span title="Expiring within 30 days">
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lead Generation (067) */}
      {leadConfigs.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Lead Generation
            </CardTitle>
            <Badge variant="secondary">{leadsTotal} lead{leadsTotal !== 1 ? "s" : ""}</Badge>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {leadConfigs.map((config) => (
                <div key={config.id} className="rounded-md border border-border p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{config.provider_name}</span>
                      <Badge variant={config.active ? "default" : "secondary"}>
                        {config.active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <span className="text-xs text-muted-foreground">{config.provider_type}</span>
                  </div>
                  <dl className="grid grid-cols-2 gap-2 text-xs mb-3">
                    <div>
                      <dt className="text-muted-foreground">CTA</dt>
                      <dd>{config.cta_label}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Tagline</dt>
                      <dd>{config.tagline ?? "--"}</dd>
                    </div>
                  </dl>
                  {/* Logo */}
                  <div className="border-t border-border pt-3">
                    <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <Image className="h-3 w-3" /> Logo
                    </p>
                    {config.logo_url ? (
                      <div className="flex items-center gap-3">
                        <img src={config.logo_url} alt={`${config.provider_name} logo`} className="h-10 max-w-[120px] object-contain rounded border border-border" />
                        <Button variant="ghost" size="sm" className="text-destructive h-8" onClick={() => handleDeleteLogo(config.id)}>
                          <Trash2 className="h-3 w-3 mr-1" /> Remove
                        </Button>
                      </div>
                    ) : (
                      <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors w-fit">
                        <Upload className="h-3 w-3" />
                        {uploadingLogo === config.id ? "Uploading..." : "Upload logo"}
                        <input type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" className="hidden" disabled={uploadingLogo === config.id} onChange={(e) => { const file = e.target.files?.[0]; if (file) handleLogoUpload(config.id, file); }} />
                      </label>
                    )}
                  </div>

                  {/* School Assignments */}
                  <div className="border-t border-border pt-3 mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <GraduationCap className="h-3 w-3" /> Schools
                      </p>
                      <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setAddingSchool(addingSchool === config.id ? null : config.id)}>
                        <Plus className="h-3 w-3 mr-1" /> Add
                      </Button>
                    </div>

                    {addingSchool === config.id && (
                      <div className="mb-2 relative">
                        <Input
                          placeholder="Type to search schools..."
                          value={schoolSearch}
                          onChange={(e) => handleSearchSchools(config.id, e.target.value)}
                          className="h-8 text-sm"
                          autoFocus
                        />
                        {schoolSearch.length >= 2 && (
                          <div className="absolute z-10 mt-1 w-full rounded-md border border-border bg-background shadow-md max-h-40 overflow-y-auto">
                            {searchingSchools ? (
                              <p className="px-3 py-2 text-xs text-muted-foreground">Searching...</p>
                            ) : schoolResults.length === 0 ? (
                              <p className="px-3 py-2 text-xs text-muted-foreground">No schools found</p>
                            ) : (
                              schoolResults.map((s) => (
                                <button
                                  type="button"
                                  key={s.id}
                                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted truncate"
                                  onClick={() => handleAssignSchool(config.id, s.id)}
                                >
                                  {s.name}
                                </button>
                              ))
                            )}
                          </div>
                        )}
                        {schoolSearch.length > 0 && schoolSearch.length < 2 && (
                          <p className="text-xs text-muted-foreground mt-1">Type at least 2 characters to search</p>
                        )}
                      </div>
                    )}

                    {(schoolsByConfig[config.id] ?? []).length === 0 ? (
                      <p className="text-xs text-muted-foreground">No schools assigned -- ad won't show anywhere.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {(schoolsByConfig[config.id] ?? []).map((s) => (
                          <span key={s.id} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-xs">
                            {s.school_name}
                            <button type="button" title={`Remove ${s.school_name}`} onClick={() => handleRemoveSchool(config.id, s.school_id)} className="text-muted-foreground hover:text-destructive">
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lead Submissions (067) */}
      {leadConfigs.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Lead Submissions</CardTitle>
            <Badge variant="secondary">{leadsTotal} total</Badge>
          </CardHeader>
          <CardContent>
            <LeadSubmissionsTable leads={leads} total={leadsTotal} contextColumn="school" />
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {metrics.active_placements === 0 && metrics.by_status.reserved === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              No placements yet. Contact your account manager to get started.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
